import { ChevronLeftIcon } from "@heroicons/react/24/outline"
import { Button } from "antd"
import { Button as CustomButton, H4 } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useCallback, useMemo, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useAppNotificationStore } from "stores"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import StepsMainLayout from "../setup/components/StepsMainLayout"
import SetupSteps from "./SetupSteps"
import UploadTimesheet from "./UploadTimeSheetModal"
import MapColumn from "./MapColumnModal"
import {
  OPTIONAL_FIELDS,
  REQUIRED_FIELDS,
  SETUP_STEPS,
} from "./upload-timesheet/constants"
import {
  computeInvalidMap,
  computeRecordsForSubmission,
  computeRecordsToOverride,
  countSkippedRecords,
  getOptionsFor,
  isMapComplete as computeIsMapComplete,
} from "./upload-timesheet/utils"
import { mapRowsInWorker } from "./upload-timesheet/useTimesheetRowMapper"
import type {
  AttendanceRecord,
  ColumnMap,
  DuplicateAction,
  UploadTimeSheetFailedEntity,
} from "./upload-timesheet/types"
import AffectedEmployeesDrawer from "./upload-timesheet/drawers/AffectedEmployeesDrawer"
import DuplicateConflictsDrawer from "./upload-timesheet/drawers/DuplicateConflictsDrawer"
import Step3Review from "./upload-timesheet/steps/Step3Review"

const UploadTimeSheetStep = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()

  const [currentStep, setCurrentStep] = useState(0)
  const [viewingFailedType, setViewingFailedType] = useState<string | null>(
    null
  )
  const [duplicateResolutions, setDuplicateResolutions] = useState<
    Record<string, DuplicateAction>
  >({})

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [records, setRecords] = useState<Record<string, unknown>[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [columnMap, setColumnMap] = useState<ColumnMap>({})
  const [mappedRecords, setMappedRecords] = useState<AttendanceRecord[]>([])

  const [failedUpload, setFailedUpload] = useState<
    UploadTimeSheetFailedEntity[]
  >([])
  const [isMapping, setIsMapping] = useState(false)
  const [showEmptyPayloadCta, setShowEmptyPayloadCta] = useState(false)
  const [showUnresolvedConflictsModal, setShowUnresolvedConflictsModal] =
    useState(false)

  const isMapComplete = useMemo(
    () => computeIsMapComplete(columnMap),
    [columnMap]
  )
  const invalidMap = useMemo(() => computeInvalidMap(columnMap), [columnMap])
  const hasInvalid = useMemo(
    () => Object.values(invalidMap).some(Boolean),
    [invalidMap]
  )
  const optionsFor = useCallback(
    (stateKey: string) => getOptionsFor(headers, columnMap, stateKey),
    [headers, columnMap]
  )

  const { mutate: uploadTimeSheetDebug, isLoading: isLoadingUploadTimeSheet } =
    useMutation({
      mutationFn: (records: AttendanceRecord[]) =>
        ewaAxiosInstance.post(`/v1/time-attendance/timesheets/upload/debug`, {
          records,
        }),
      onSuccess: (data) => {
        const failed = data?.data?.data?.failed || []
        setFailedUpload(failed)
        queryClient.invalidateQueries({
          queryKey: [`/v1/time-attendance/workplaces`],
        })
        // Only advance to Review once we have a successful debug response.
        handleNextStep()
      },
      onError: onQueryError,
    })

  const { mutate: submitTimeSheet, isLoading: isSubmittingTimeSheet } =
    useMutation({
      mutationFn: (body: {
        records: AttendanceRecord[]
        recordsToOverride?: (number | string)[]
      }) =>
        ewaAxiosInstance.post(`/v1/time-attendance/timesheets/upload`, body),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [`/v1/time-attendance/workplaces`],
        })
        toast.success(data?.data?.message || "Timesheet uploaded successfully")
        navigate("/dashboard/time-and-attendance/overview")
      },
      onError: onQueryError,
    })

  const handleNextStep = () =>
    setCurrentStep((prev) => (prev < SETUP_STEPS.length - 1 ? prev + 1 : prev))

  const handleBack = () =>
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev))

  const handleImport = async () => {
    setIsMapping(true)
    try {
      const mapped = await mapRowsInWorker(records, columnMap)
      setMappedRecords(mapped)
      uploadTimeSheetDebug(mapped)
    } catch (error) {
      console.error("Row mapping failed:", error)
      toast.error("Could not prepare records for upload.")
    } finally {
      setIsMapping(false)
    }
  }

  const recordsToSubmit = useMemo(
    () =>
      computeRecordsForSubmission(
        mappedRecords,
        failedUpload,
        duplicateResolutions
      ),
    [mappedRecords, failedUpload, duplicateResolutions]
  )
  const skippedCount = useMemo(
    () => countSkippedRecords(failedUpload, duplicateResolutions),
    [failedUpload, duplicateResolutions]
  )

  const performSubmit = () => {
    if (isSubmittingTimeSheet) return
    const recordsToOverride = computeRecordsToOverride(
      failedUpload,
      duplicateResolutions
    )
    submitTimeSheet({
      records: recordsToSubmit,
      ...(recordsToOverride.length > 0 && { recordsToOverride }),
    })
  }

  const handleSubmit = () => {
    if (recordsToSubmit.length === 0) {
      setShowEmptyPayloadCta(true)
      return
    }
    if (skippedCount > 0) {
      setShowUnresolvedConflictsModal(true)
      return
    }
    performSubmit()
  }

  const resetDownstreamState = () => {
    setColumnMap({})
    setMappedRecords([])
    setFailedUpload([])
    setDuplicateResolutions({})
    setViewingFailedType(null)
  }

  const resetUploadState = () => {
    setSelectedFile(null)
    setRecords([])
    setHeaders([])
    resetDownstreamState()
  }

  const handleUploadNew = () => {
    setShowEmptyPayloadCta(false)
    resetUploadState()
    setCurrentStep(0)
  }

  const handleSetResolution = (keys: string[], action: DuplicateAction) => {
    setDuplicateResolutions((prev) => {
      const next = { ...prev }
      keys.forEach((k) => {
        next[k] = action
      })
      return next
    })
  }

  const closeAffectedDrawer = () => {
    setViewingFailedType(null)
  }

  const activeFailed = failedUpload.find((f) => f.type === viewingFailedType)

  return (
    <StepsMainLayout
      sidebar={
        <SetupSteps
          title="Upload steps"
          steps={SETUP_STEPS}
          currentStep={currentStep}
        />
      }
      isBusy={isLoadingUploadTimeSheet || isMapping}
    >
      {currentStep === 0 && (
        <>
          <H4>Upload Timesheet</H4>
          <UploadTimesheet
            selectedFile={selectedFile}
            onFileChange={(file) => {
              setSelectedFile(file)
              if (!file) {
                setHeaders([])
                setRecords([])
                resetDownstreamState()
              }
            }}
            onParse={(parsedHeaders, parsedRecords) => {
              setHeaders(parsedHeaders)
              setRecords(parsedRecords)
              resetDownstreamState()
            }}
            handleNextStep={handleNextStep}
          />
        </>
      )}
      {currentStep === 1 && (
        <>
          <Button
            type="default"
            className="self-start font-semibold px-4 py-2 h-auto rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] flex items-center gap-2 mb-4"
            icon={<ChevronLeftIcon className="w-4 h-4" />}
            onClick={handleBack}
          >
            Go back
          </Button>
          <MapColumn
            onImport={handleImport}
            isLoading={isLoadingUploadTimeSheet || isMapping}
            isMapComplete={isMapComplete}
            hasInvalid={hasInvalid}
            requiredFields={REQUIRED_FIELDS}
            optionalFields={OPTIONAL_FIELDS}
            columnMap={columnMap}
            invalidMap={invalidMap}
            optionsFor={optionsFor}
            onColumnMapChange={(stateKey, value) =>
              setColumnMap((prev) => ({ ...prev, [stateKey]: value }))
            }
          />
        </>
      )}
      {currentStep === 2 && (
        <Step3Review
          isLoading={isLoadingUploadTimeSheet}
          isSubmitting={isSubmittingTimeSheet}
          failedUpload={failedUpload}
          onBack={handleBack}
          onView={(failedType) => setViewingFailedType(failedType)}
          onSubmit={handleSubmit}
        />
      )}

      <AffectedEmployeesDrawer
        open={!!viewingFailedType && viewingFailedType !== "duplicate_entry"}
        failed={activeFailed}
        failedType={viewingFailedType}
        onClose={closeAffectedDrawer}
      />

      <DuplicateConflictsDrawer
        open={viewingFailedType === "duplicate_entry"}
        failed={activeFailed}
        resolutions={duplicateResolutions}
        onSetResolution={handleSetResolution}
        onClose={closeAffectedDrawer}
      />

      <CustomModal
        open={showEmptyPayloadCta}
        onCancel={() => setShowEmptyPayloadCta(false)}
        title="No records to submit"
        centered
        footer={
          <div className="w-full justify-end flex gap-4">
            <CustomButton
              color="neutral"
              onClick={() => setShowEmptyPayloadCta(false)}
            >
              Cancel
            </CustomButton>
            <CustomButton
              color="primary"
              onClick={handleUploadNew}
            >
              Upload new file
            </CustomButton>
          </div>
        }
      >
        <p className="text-sm text-slate-500 m-0">
          All records in this sheet were rejected. Upload a new file to
          continue, or cancel to keep reviewing this one.
        </p>
      </CustomModal>

      <CustomModal
        open={showUnresolvedConflictsModal}
        onCancel={() => setShowUnresolvedConflictsModal(false)}
        title="Confirm action"
        width={440}
        centered
        footer={
          <div className="w-full flex flex-col gap-2">
            <CustomButton
              color="primary"
              className="!w-full"
              disabled={isSubmittingTimeSheet}
              // @ts-ignore
              submitting={isSubmittingTimeSheet}
              onClick={() => {
                if (isSubmittingTimeSheet) return
                setShowUnresolvedConflictsModal(false)
                performSubmit()
              }}
            >
              Submit anyway
            </CustomButton>
            <CustomButton
              color="neutral"
              className="!w-full"
              disabled={isSubmittingTimeSheet}
              onClick={() => setShowUnresolvedConflictsModal(false)}
            >
              Go back and resolve
            </CustomButton>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <h3 className="font-bold text-lg text-[#0F172B] m-0">
            You still have unresolved records
          </h3>
          <p className="text-sm text-[#62748E] m-0">
            {`${skippedCount} ${
              skippedCount === 1 ? "record hasn't" : "records haven't"
            } been resolved yet. Unresolved rows will be skipped and won't be imported.`}
          </p>
        </div>
      </CustomModal>
    </StepsMainLayout>
  )
}

export default UploadTimeSheetStep
